import type {
  Action,
  ActionFromReducersMapObject,
  AnyAction,
  Reducer,
  ReducersMapObject,
  StateFromReducersMapObject,
  Store,
} from 'redux';
import { combineReducers } from 'redux';

export interface ReducerWithMap<S> extends Reducer<S, AnyAction> {
  reducers: ReducersMapObject;
}

export function combineReducersWithMap<S>(
  reducers: ReducersMapObject<S, any>
): Reducer<S> & { reducers: ReducersMapObject<S> };
export function combineReducersWithMap<S, A extends Action = AnyAction>(
  reducers: ReducersMapObject<S, A>
): Reducer<S, A> & { reducers: ReducersMapObject<S, A> };
export function combineReducersWithMap<M extends ReducersMapObject>(
  reducers: M
): Reducer<StateFromReducersMapObject<M>, ActionFromReducersMapObject<M>> & {
  reducers: M;
};
export function combineReducersWithMap<S>(reducers: ReducersMapObject): ReducerWithMap<S> {
  const reducer = combineReducers(reducers);
  Object.defineProperty(reducer, 'reducers', { value: reducers, writable: false });
  return reducer as Reducer<S, AnyAction> & { reducers: ReducersMapObject };
}

export type StoreWithReducerInjector<T extends Store = Store<any, AnyAction>> = T & {
  injectReducer: (key: string, reducer: Reducer) => void;
};

export function attachReducerInjector<T extends Store>(
  store: T,
  staticReducers: ReducersMapObject
): StoreWithReducerInjector<T> {
  const asyncReducers: Record<string, Reducer> = {};
  return {
    ...store,
    injectReducer: (key, reducer) => {
      asyncReducers[key] = reducer;
      store.replaceReducer(combineReducers({ ...staticReducers, ...asyncReducers }));
    },
  };
}
